import type { LaseredClaimantListItem, LaseredCondition, Policy, PolicyData } from "@/types";
import type { DeductibleReview, DeductibleReviewCandidate } from "@/types/deductibleReview";
import type { DeductibleSource } from "@/resources/claimants/hooks/useCurrentPolicyData";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { DATE_FORMATS, formatDateRange, formatLongDate } from "@/utils/formatters/dateFormatter";
import { conditionDisplayState } from "@/utils/helpers/expectedTrigger";

/**
 * Row model for the claimant Deductible tab (#7661, design mockup-v6 V1).
 *
 * Pure: everything here is derived from plain inputs so the placement of each
 * rung can be unit-tested per source without rendering. The ladder is the
 * engine's (coronis-apps `queries/policy_data.sql`, confirmed by the calc
 * expert): contract exclusion › contract laser › review decision › (held: the
 * last settled value while a review is open) › conditional laser (latest
 * trigger date) › straight laser › policy standard. Deductible modification
 * endorsements are not in the ladder: the engine never reads them.
 *
 * The applied amount and source are NEVER derived here: they come from
 * `getEffectiveDeductible`, the one FE resolver. This file only decides which
 * row carries the applied marker and which rows sit under or beside it.
 */

export const money = (n?: number | null): string =>
  typeof n === "number" ? formatUSD(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : "—";

/** A DATE field (YYYY-MM-DD, maybe with a midnight time): the calendar day as stored. */
export const day = (d?: string | null): string | undefined => (d ? formatLongDate(d.slice(0, 10)) : undefined);
/** A timestamp: the user's local date. */
export const stamp = (d?: string | null): string | undefined => (d ? formatLongDate(d) : undefined);
export const dateWindow = (start?: string, end?: string): string | undefined =>
  start || end ? formatDateRange(start, end, DATE_FORMATS.DISPLAY_LONG) : undefined;

export type DeductibleRowKind = "laser" | "policy" | "conditional" | "contract" | "review" | "modification";

export interface DeductibleRow {
  /** Stable id, also the expand/collapse key. */
  id: string;
  kind: DeductibleRowKind;
  title: string;
  subtitle?: string;
  /** Right-hand text: an amount or a short state. Absent for a full exclusion. */
  value?: string;
  /** This row is the source of the applied amount. */
  applied: boolean;
  /** Has content behind it: renders as a button with a chevron. */
  expandable: boolean;
  /** Placeholder rows while a list loads or after it fails. */
  status?: "loading" | "error";
  condition?: LaseredCondition;
  review?: DeductibleReview;
}

export type AsyncList<T> = { status: "loading" } | { status: "error" } | { status: "ready"; data: T[] };

export interface DeductibleRowsInput {
  source: DeductibleSource;
  amount: number;
  reviewPending: boolean;
  policy?: Policy;
  policyData?: PolicyData;
  conditions: AsyncList<LaseredCondition>;
  reviews: AsyncList<DeductibleReview>;
  /** The claimant's row from `GET /policies/{id}/lasered-claimants/{claimantId}`, when fetched. */
  laserRow?: LaseredClaimantListItem;
  /** That read is still in flight: the laser row shows skeletons instead of a subtitle that then grows. */
  laserLoading?: boolean;
  /** "Summit Re 2026": the policy's display label for the standard row. */
  policyLabel?: string;
}

export interface DeductibleRowGroups {
  why: DeductibleRow[];
  couldChange: DeductibleRow[];
}

// ─── Derived facts ──────────────────────────────────────────────────────────

/** The straight laser's facts, preferring the claimant snapshot and filling gaps from the list row. */
export const straightLaserFacts = (policyData?: PolicyData, laserRow?: LaseredClaimantListItem) => {
  const attrs = policyData?.lasered_attributes;
  const amount = attrs?.amt_lasered_deductible ?? laserRow?.amt_lasered_deductible;
  const present = !!policyData?.is_lasered && typeof amount === "number" && amount > 0;
  if (!present) return undefined;
  return {
    amount,
    incremental: attrs?.amt_incremental_lasered_deductible ?? laserRow?.amt_incremental_lasered_deductible ?? 0,
    laserType: attrs?.laser_type ?? laserRow?.laser_type,
    reason: attrs?.laser_reason ?? laserRow?.laser_reason,
    /**
     * The laser's effective date is reliably present only on the lasered-claimants
     * row; the claimant GET usually omits it. When the laser is the applied source
     * the snapshot's `effective_deductible_date` is the same date (policy_data.sql).
     */
    effectiveDate: attrs?.effective_date ?? laserRow?.effective_date,
  };
};

export const laserEffectiveDate = (
  source: DeductibleSource,
  policyData?: PolicyData,
  laserRow?: LaseredClaimantListItem
): string | undefined => {
  const facts = straightLaserFacts(policyData, laserRow);
  if (facts?.effectiveDate) return facts.effectiveDate;
  return source === "LASER" ? policyData?.effective_deductible_date : undefined;
};

export const contractFacts = (policyData?: PolicyData) => {
  const excluded = !!policyData?.is_contract_excluded;
  const lasered = !excluded && !!policyData?.is_contract_lasered;
  if (!excluded && !lasered) return undefined;
  return {
    excluded,
    lasered,
    amount: policyData?.amt_contract_lasered_deductible,
    incurred: dateWindow(policyData?.contract_incurred_start_date, policyData?.contract_incurred_end_date),
    paid: dateWindow(policyData?.contract_paid_start_date, policyData?.contract_paid_end_date),
    excludedProcedures: policyData?.excluded_procedures ?? [],
  };
};

/**
 * The condition the engine applied: `is_effective`, else the latest triggered
 * one. While a review is open the fallback would name the NEW trigger, not the
 * one held (same-date tie, increase after submission), so then only
 * `is_effective` counts.
 */
export const appliedCondition = (conditions: LaseredCondition[], held = false): LaseredCondition | undefined =>
  conditions.find((c) => c.is_effective) ??
  (held
    ? undefined
    : conditions
        .filter((c) => c.lasered_condition_trigger_state === "triggered")
        .sort((a, b) => (b.trigger_date ?? "").localeCompare(a.trigger_date ?? ""))[0]);

/** The newest decided review: resolved first, else superseded. */
export const newestDecidedReview = (reviews: DeductibleReview[]): DeductibleReview | undefined =>
  reviews.find((r) => r.status === "resolved") ?? reviews.find((r) => r.status === "superseded");

export const chosenCandidate = (review?: DeductibleReview): DeductibleReviewCandidate | undefined =>
  review?.candidates?.find((c) => c.id === review.chosen_candidate_id);

const conditionById = (conditions: LaseredCondition[], id?: string) =>
  id ? conditions.find((c) => c.id === id) : undefined;

// ─── Hero ───────────────────────────────────────────────────────────────────

export interface HeroLeadInput {
  source: DeductibleSource;
  reviewPending?: boolean;
  policy?: Policy;
  policyData?: PolicyData;
  conditions: LaseredCondition[];
  reviews: DeductibleReview[];
  laserRow?: LaseredClaimantListItem;
}

const laserSentence = (base: number | undefined, effective: string | undefined) => {
  const when = effective ? `, effective ${day(effective)}` : "";
  const over = typeof base === "number" ? `, over the policy's ${money(base)} standard` : "";
  return `Lasered${when}${over}`;
};

const conditionSentence = (c: LaseredCondition | undefined, trigger = c?.trigger_date) => {
  const on = trigger ? ` on ${day(trigger)}` : "";
  const name = c?.lasered_condition_name;
  // An expected trigger the analyst set counts as a trigger for the engine, but it is not a claim.
  if (c && conditionDisplayState(c) === "expected") return `Set by the expected ${name} trigger${on}, no claim yet`;
  return name ? `Set by a triggered ${name} condition${on}` : `Set by a triggered conditional laser${on}`;
};

/** The hero sentence while a deductible review is open (every source). */
export const HELD_LEAD = "Held at the last settled deductible until this review is resolved.";

/**
 * One sentence under the hero amount, by source. While a review is open the
 * engine applies the last settled deductible, whatever its source was
 * (policy_data.sql: COALESCE(prior_effective_specific_deductible, standing);
 * the source keeps the prior's value, there is no HELD source), so the sentence
 * says "held" and the applied row below names the prior.
 */
export const heroLead = (input: HeroLeadInput): string => {
  const { source, policy, policyData, conditions, reviews, laserRow, reviewPending = false } = input;
  if (reviewPending && source !== "EXCLUDED") return HELD_LEAD;
  const base = policy?.specific_benefit?.specific_deductible ?? undefined;
  switch (source) {
    case "BASE":
      return "The policy's standard specific deductible.";
    case "LASER":
      return `${laserSentence(base, laserEffectiveDate(source, policyData, laserRow))}.`;
    case "CONDITIONAL": {
      return `${conditionSentence(appliedCondition(conditions, reviewPending))}.`;
    }
    case "CONTRACT": {
      const f = contractFacts(policyData);
      const windows =
        f?.incurred || f?.paid
          ? ` Only claims incurred ${f.incurred ?? "in the contract window"} and paid ${f.paid ?? "in the contract window"} count.`
          : "";
      return `Set by the carrier contract.${windows}`;
    }
    case "EXCLUDED":
      return "Excluded from the carrier contract for this term.";
    case "REVIEW_DECISION": {
      const review = newestDecidedReview(reviews);
      const chosen = chosenCandidate(review);
      const decided = stamp(review?.decided_datetime ?? review?.resolved_datetime);
      const confirmed = decided ? `, confirmed by review on ${decided}.` : ", confirmed by review.";
      switch (chosen?.source) {
        case "conditional_laser": {
          const c = conditionById(conditions, chosen.source_ref_id);
          return `${conditionSentence(c, c?.trigger_date ?? chosen.effective_date)}${confirmed}`;
        }
        case "lasered":
        case "merged_laser":
          return `${laserSentence(base, chosen.effective_date ?? laserEffectiveDate(source, policyData, laserRow))}${confirmed}`;
        case "specific":
          return `The policy's standard specific deductible${confirmed}`;
        default:
          return `Chosen in a deductible review${confirmed}`;
      }
    }
    case "MODIFICATION":
      return "Modified deductible.";
  }
};

// ─── Rows ───────────────────────────────────────────────────────────────────

export const conditionStateText = (c: LaseredCondition, policyData?: PolicyData): string => {
  if (policyData?.is_contract_excluded) return "Inert under the contract exclusion";
  if (policyData?.is_contract_lasered || c.lasered_condition_trigger_state === "inert")
    return "Inert under a contract laser";
  const state = conditionDisplayState(c);
  switch (state) {
    case "triggered": {
      const on = c.trigger_date ? `Triggered ${day(c.trigger_date)}` : "Triggered";
      return c.triggered_by_claim_item_id ? `${on} · from a claim` : on;
    }
    case "expected":
      return c.trigger_date ? `Expected ${day(c.trigger_date)}` : "Expected trigger set";
    case "overdue":
      return c.expected_trigger?.review_by
        ? `Expected trigger past review-by ${day(c.expected_trigger.review_by)}`
        : "Expected trigger overdue";
    case "inert":
      return "Inert under a contract laser";
    default:
      return "Armed";
  }
};

const conditionRow = (c: LaseredCondition, applied: boolean, policyData?: PolicyData): DeductibleRow => ({
  id: `condition:${c.id}`,
  kind: "conditional",
  title: `Conditional laser · ${c.lasered_condition_name}`,
  subtitle: conditionStateText(c, policyData),
  value: money(c.amt_lasered_conditional_deductible),
  applied,
  expandable: true,
  condition: c,
});

const laserSubtitle = (
  source: DeductibleSource,
  facts: NonNullable<ReturnType<typeof straightLaserFacts>>,
  effective: string | undefined,
  chosen?: DeductibleReviewCandidate
): string | undefined => {
  const dated = effective ? `Effective ${day(effective)}` : undefined;
  switch (source) {
    case "CONTRACT":
    case "EXCLUDED":
      return "Overridden by the contract";
    case "CONDITIONAL":
      return [dated, "replaced by a triggered condition"].filter(Boolean).join(" · ");
    case "REVIEW_DECISION":
      return [
        dated,
        chosen?.source === "lasered" || chosen?.source === "merged_laser"
          ? "chosen by review"
          : "not applied by decision",
      ]
        .filter(Boolean)
        .join(" · ");
    default:
      return [dated, facts.reason].filter(Boolean).join(" · ") || undefined;
  }
};

const placeholder = (kind: "conditional" | "review", status: "loading" | "error"): DeductibleRow => ({
  id: `${kind}:${status}`,
  kind,
  title: kind === "conditional" ? "Conditional lasers" : "Deductible review",
  subtitle: status === "error" ? "Couldn't load" : undefined,
  applied: false,
  expandable: false,
  status,
});

/**
 * Which rows go where. `why` is the applied row followed by every rung it
 * overrides, in the engine's order; `couldChange` is what could replace it:
 * armed conditions, the carrier contract and the deductible review.
 */
export const buildDeductibleRows = (input: DeductibleRowsInput): DeductibleRowGroups => {
  const { source, reviewPending, policy, policyData, laserRow, policyLabel } = input;
  const why: DeductibleRow[] = [];
  const couldChange: DeductibleRow[] = [];

  const conditions = input.conditions.status === "ready" ? input.conditions.data : [];
  const reviews = input.reviews.status === "ready" ? input.reviews.data : [];

  // Contract tier: exclusion or contract laser is applied; otherwise a static row.
  const contract = contractFacts(policyData);
  const contractRow: DeductibleRow = contract
    ? contract.excluded
      ? {
          id: "contract",
          kind: "contract",
          title: "Excluded by the carrier contract",
          subtitle: contract.incurred ? `Incurred ${contract.incurred}` : undefined,
          applied: source === "EXCLUDED",
          expandable: true,
        }
      : {
          id: "contract",
          kind: "contract",
          title: "Carrier contract",
          subtitle: `Contract laser${
            contract.excludedProcedures.length
              ? ` · ${contract.excludedProcedures.length} excluded procedure${contract.excludedProcedures.length === 1 ? "" : "s"}`
              : ""
          }`,
          value: money(contract.amount),
          applied: source === "CONTRACT",
          expandable: true,
        }
    : {
        id: "contract",
        kind: "contract",
        title: "Carrier contract",
        value: "No override",
        applied: false,
        expandable: false,
      };

  // Review tier.
  const openReview = reviews.find((r) => r.status === "open");
  const decided = newestDecidedReview(reviews);
  const decidedWhen = stamp(decided?.decided_datetime ?? decided?.resolved_datetime ?? decided?.created_datetime);
  const chosen = chosenCandidate(decided);
  let reviewRow: DeductibleRow;
  if (input.reviews.status !== "ready") {
    reviewRow = placeholder("review", input.reviews.status);
  } else if (openReview || (reviewPending && !decided)) {
    const since = stamp(openReview?.created_datetime);
    // A held value can itself be an earlier decision (policy_data.sql keeps the
    // prior source): then the decision is the applied rung, under the new review.
    const holdsDecision = source === "REVIEW_DECISION" && !!decided;
    reviewRow = {
      id: "review",
      kind: "review",
      title: "Deductible review",
      subtitle: [
        since ? `Open since ${since}` : "Open",
        holdsDecision && decidedWhen ? `holding the ${decidedWhen} decision` : undefined,
      ]
        .filter(Boolean)
        .join(" · "),
      value: "On hold",
      applied: holdsDecision,
      expandable: false,
      review: openReview,
    };
  } else if (decided) {
    reviewRow = {
      id: "review",
      kind: "review",
      title: "Deductible review",
      subtitle:
        decided.status === "superseded"
          ? `Superseded${decidedWhen ? ` ${decidedWhen}` : ""}`
          : `Decided${decidedWhen ? ` ${decidedWhen}` : ""}`,
      value: chosen ? money(chosen.amt_deductible) : decided.status === "superseded" ? "Superseded" : "Decided",
      applied: source === "REVIEW_DECISION",
      expandable: true,
      review: decided,
    };
  } else {
    reviewRow = {
      id: "review",
      kind: "review",
      title: "Deductible review",
      value: "None this term",
      applied: false,
      expandable: false,
    };
  }

  // Conditional tier.
  const applied = appliedCondition(conditions, reviewPending);
  const conditionRows =
    input.conditions.status !== "ready"
      ? [placeholder("conditional", input.conditions.status)]
      : conditions.map((c) => conditionRow(c, source === "CONDITIONAL" && c.id === applied?.id, policyData));
  // Under a decision, the triggered conditions are rungs the decision overrides
  // (or confirmed); under a contract tier, every condition is inert and sits aside.
  const isWhyCondition = (row: DeductibleRow) => {
    if (!row.condition) return source === "CONDITIONAL" && row.status !== undefined;
    if (source === "CONDITIONAL") return row.condition.id === applied?.id;
    if (source === "REVIEW_DECISION")
      return (
        row.condition.lasered_condition_trigger_state === "triggered" ||
        (chosen?.source === "conditional_laser" && chosen.source_ref_id === row.condition.id)
      );
    return false;
  };

  // Straight laser tier.
  const laser = straightLaserFacts(policyData, laserRow);
  const straightRow: DeductibleRow | undefined = laser
    ? {
        id: "laser",
        kind: "laser",
        title: "Laser",
        subtitle: laserSubtitle(source, laser, laserEffectiveDate(source, policyData, laserRow), chosen),
        value: money(laser.amount),
        applied: source === "LASER",
        expandable: true,
        status: input.laserLoading ? "loading" : undefined,
      }
    : undefined;

  const policyStandard: DeductibleRow = {
    id: "policy",
    kind: "policy",
    title: "Policy standard",
    subtitle: policyLabel || undefined,
    value: money(policy?.specific_benefit?.specific_deductible ?? undefined),
    applied: source === "BASE",
    expandable: true,
  };

  // Assemble in engine order. The applied rung and everything under it go to
  // "why"; the tiers above it that did not fire go to "could change".
  if (contractRow.applied) why.push(contractRow);
  else couldChange.push(contractRow);

  if (reviewRow.applied) why.push(reviewRow);
  else couldChange.push(reviewRow);

  for (const row of conditionRows) {
    if (isWhyCondition(row)) why.push(row);
    else couldChange.push(row);
  }

  // The engine's vocabulary still carries `modification` (it appears in
  // prior_effective_deductible_source) though no compute path applies one today
  // (#6508 DM1 not built). Should one arrive as the source, it gets its own
  // applied row rather than an applied marker that lands nowhere.
  if (source === "MODIFICATION") {
    why.push({
      id: "modification",
      kind: "modification",
      title: "Modified deductible",
      subtitle: "Deductible modification endorsement",
      value: money(input.amount),
      applied: true,
      expandable: false,
    });
  }

  if (straightRow) why.push(straightRow);
  why.push(policyStandard);

  // A held value whose prior rung no longer shows as applied (a reversed
  // trigger, a removed laser: the API sets is_effective only for the current
  // trigger set) is still held by the review, so the review row carries the
  // marker and names the amount (review !5050, expert medium 2).
  if (reviewPending && openReview && ![...why, ...couldChange].some((r) => r.applied)) {
    const review = couldChange.find((r) => r.id === "review");
    if (review) {
      review.applied = true;
      review.subtitle = [review.subtitle, `holding ${money(input.amount)}`].filter(Boolean).join(" · ");
    }
  }

  // Could-change reads: conditions, contract, review (the design's order).
  couldChange.sort((a, b) => ORDER[a.kind] - ORDER[b.kind]);
  return { why, couldChange };
};

const ORDER: Record<DeductibleRowKind, number> = {
  conditional: 0,
  contract: 1,
  review: 2,
  modification: 3,
  laser: 4,
  policy: 5,
};

/** Short reason for a decided review's detail. */
export const reviewReasonText = (review: DeductibleReview): string => {
  switch (review.reason) {
    case "same_date_tie":
      return "Two deductibles took effect on the same date with different amounts";
    case "increase_after_submission":
      return "The deductible would have increased after a stop-loss claim was submitted";
    case "manual_after_trigger":
      return "A straight laser was entered after a conditional laser triggered";
    case "merged_laser_conflict":
      return "Two merged claimants had different straight lasers";
    case "expected_trigger_overdue":
      return "An expected trigger passed its review-by date with no claim";
    default:
      return "Deductible needed review";
  }
};
